/**
 * PaintActivityPanel — job-level summary of all paint batches.
 *
 * Renders nothing when there are no paint requests; no empty-state
 * placeholder. Each row opens PaintRequestDetailModal on click.
 */

const STATUS_PILL_STYLES = {
  pending: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  quoted: 'bg-yellow-100 text-yellow-700',
  ready_for_pickup: 'bg-orange-100 text-orange-700',
  received: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500 line-through',
};

const STATUS_LABELS = {
  pending: 'Pending',
  sent: 'Sent',
  quoted: 'Quoted',
  ready_for_pickup: 'Ready for Pickup',
  received: 'Received',
  cancelled: 'Cancelled',
};

export function PaintStatusBadge({ status, size = 'sm' }) {
  const style = STATUS_PILL_STYLES[status] || 'bg-gray-100 text-gray-600';
  const label = STATUS_LABELS[status] || status;
  const sizing = size === 'lg'
    ? 'px-2.5 py-1 text-xs'
    : 'px-2 py-0.5 text-[11px]';
  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizing} ${style}`}>
      {label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function fmtQty(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n % 1 === 0 ? String(Math.round(n)) : n.toString();
}

export default function PaintActivityPanel({ paintRequests, onRowClick }) {
  if (!paintRequests || paintRequests.length === 0) return null;

  return (
    <div className="mx-4 mt-3 bg-white rounded-lg border border-purple-200">
      <div className="px-4 py-2 border-b border-purple-100 text-sm font-semibold text-purple-800 flex items-center gap-2">
        <span>🎨</span>
        <span>Paint Activity</span>
        <span className="text-xs font-normal text-gray-500">
          ({paintRequests.length} batch{paintRequests.length !== 1 ? 'es' : ''})
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {paintRequests.map((pr) => (
          <button
            key={pr.id}
            onClick={() => onRowClick?.(pr)}
            className="w-full px-4 py-2 flex items-center gap-4 text-left hover:bg-purple-50/50 transition-colors"
          >
            <span className="font-mono text-sm text-blue-700 shrink-0">
              {pr.paint_request_number}
            </span>
            <span className="text-xs text-gray-500 shrink-0">
              Sent {fmtDate(pr.sent_at)}
            </span>
            <span className="text-sm text-gray-700 shrink-0">
              Qty {fmtQty(pr.qty_sent)}
            </span>
            <span className="text-xs text-gray-500 truncate">
              {pr.vendor_name}
            </span>
            <span className="ml-auto shrink-0">
              <PaintStatusBadge status={pr.status} />
            </span>
            <span className="text-gray-400 shrink-0">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}
